// PerformanceVideoPlayer anchors the Video Performance Direction workflow, letting reviewers
// sync translated audio against the master video with parent-driven play/pause, speed, and
// drift-correction controls tied to an external timing source.
"use strict";
import "react-video-seek-slider/styles.css";
import { VideoSeekSlider } from "react-video-seek-slider";
import { useCallback, useEffect, useRef, useState } from "react";
import { Grid, Stack } from "@mui/material";
import classes from "../../Assets/Styles/VoiceQAScreen.module.css";
import Slider from "@mui/material/Slider";
import VolumeDown from "@mui/icons-material/VolumeDown";
import VolumeUp from "@mui/icons-material/VolumeUp";
import FastRewindIcon from "@mui/icons-material/FastRewind";
import FastForwardIcon from "@mui/icons-material/FastForward";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import { primaryColor } from "../../utils/constants";
import { PLAYBACK_SPEED_OPTIONS } from "../../utils/playbackSpeedOptions";
import { parseTimestampAsTotalSeconds } from "../../utils/generalFunctions";
import InputMask from "react-input-mask";

/**
 * Props:
 * - source: string
 * - displayAudioTime: (s: string) => void
 * - seekTime: number (seconds)
 * - seekTimeIndex: any
 * - goToPageAccordingTimeAPI?: (timeSeconds: number) => void
 * - syncPreview: boolean
 * - setSyncPreview: (b: boolean) => void
 * - playPausePlayer: "play" | "pause"
 * - manualPauseTick?: number
 * - masterMs?: number
 * - onSyncPlay?: () => void           // NEW: parent plays the audio
 * - onSyncPause?: () => void          // NEW: parent pauses the audio
 * - onSyncSetSpeed?: (rate:number) => void // NEW: parent sets audio speed
 */

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

// Sync thresholds (ms)
const SOFT_DRIFT_MS = 80;
const HARD_DRIFT_MS = 300;
const HARD_COOLDOWN_MS = 800;

const PerformanceVideoPlayer = (props) => {
  const player = useRef(null);
  const lastHardSyncAt = useRef(0);

  const [currentTime, setCurrentTime] = useState(0); // ms
  const [progress, setProgress] = useState(0);       // ms buffered
  const [maxTime, setMaxTime] = useState(0);         // ms
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.5);
  const playStateRef = useRef(props.playPausePlayer);
  const volumeRef = useRef(volume);
  const [audioSpeed, setAudioSpeed] = useState(1);
  const [inputTime, setInputTime] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // Fixed header offset
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY < 95) setIsScrolled(108 - window.scrollY);
      else setIsScrolled(10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleTimeChange = useCallback((timeMs) => {
    const v = player.current;
    if (!v) return;
    const nextMs = clamp(Number(timeMs) || 0, 0, maxTime || 0);
    v.currentTime = nextMs / 1000;
    setCurrentTime(nextMs);
  }, [maxTime]);

  const onProgress = useCallback(() => {
    const v = player.current;
    const buf = v?.buffered;
    if (!v || !buf || buf.length === 0) return;
    const t = v.currentTime;
    let idx = 0;
    for (; idx < buf.length; idx++) {
      if (buf.start(idx) <= t && t <= buf.end(idx)) break;
    }
    const end = buf.end(Math.min(idx, buf.length - 1));
    setProgress((end || 0) * 1000);
  }, []);

  const onLoadedMeta = useCallback(() => {
    const v = player.current;
    setMaxTime((v?.duration || 0) * 1000);
  }, []);

  // Smooth UI time while playing
  useEffect(() => {
    let raf;
    const step = () => {
      const v = player.current;
      if (v) setCurrentTime(v.currentTime * 1000);
      raf = requestAnimationFrame(step);
    };
    if (isPlaying) raf = requestAnimationFrame(step);
    return () => raf && cancelAnimationFrame(raf);
  }, [isPlaying]);

  // Reflect in parent
  useEffect(() => {
    props.displayAudioTime(((currentTime || 0) / 1000).toFixed(2));
  }, [currentTime]); // eslint-disable-line

  // External seek
  useEffect(() => {
    const v = player.current;
    if (!v) return;
    const tSec = props.seekTime ?? 0;
    v.currentTime = tSec;
    setCurrentTime(tSec * 1000);
  }, [props.seekTimeIndex]); // eslint-disable-line

  // Time input (masked)
  const handleTimeInputChange = (e) => {
    setInputTime(e.target.value);
    setIsEditing(true);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      const enteredMs = (parseTimestampAsTotalSeconds(inputTime) || 0) * 1000;
      const seekMs = clamp(enteredMs, 0, maxTime || 0);

      const v = player.current;
      if (!v) return;

      const wasPaused = v.paused;
      v.currentTime = seekMs / 1000;
      setCurrentTime(seekMs);
      setIsEditing(false);
      props.goToPageAccordingTimeAPI?.(seekMs / 1000);

      if (wasPaused) {
        v.pause();
        setIsPlaying(false);
      } else {
        v.play().then(() => setIsPlaying(true)).catch(() => { });
      }
    }
  };

  useEffect(() => {
    if (!isEditing) {
      const formatted = new Date(currentTime).toISOString().substr(11, 12);
      setInputTime(formatted);
    }
  }, [currentTime, isEditing]);

  // Volume
  const handleVolumeChange = (val) => {
    const v = player.current;
    const newVolume = typeof val === "number" ? val : parseFloat(val || 0);
    const clamped = clamp(newVolume, 0, 1);
    if (v) v.volume = clamped;
    setVolume(clamped);
  };

  useEffect(() => {
    volumeRef.current = volume;
  }, [volume]);

  // Keep latest requested play state available for source reloads without
  // forcing the player to re-initialize whenever the parent toggles play/pause.
  useEffect(() => {
    playStateRef.current = props.playPausePlayer;
  }, [props.playPausePlayer]);

  // Reload the HTML5 video element only when a different project source is
  // provided so the newly selected media starts from the beginning without
  // wiping out manual seeks whenever the play/pause state changes.
  useEffect(() => {
    const v = player.current;
    if (!v || !props.source) return;

    const shouldAutoplay = playStateRef.current === "play";
    const startVolume = clamp(volumeRef.current, 0, 1);

    v.pause();
    v.load();
    v.currentTime = 0;
    v.volume = startVolume;
    setCurrentTime(0);
    setProgress(0);
    setMaxTime(0);
    setIsPlaying(false);

    if (shouldAutoplay) {
      v
        .play()
        .then(() => setIsPlaying(true))
        .catch(() => setIsPlaying(false));
    }
  }, [props.source]);

  // Speed (re-enabled during sync; can also notify parent to set audio rate)
  const handleSpeedChange = (e) => {
    const v = player.current;
    const newSpeed = parseFloat(e.target.value);
    if (v) v.playbackRate = newSpeed;
    setAudioSpeed(newSpeed);
    if (props.syncPreview) {
      props.onSyncSetSpeed?.(newSpeed); // let parent apply to audio too
    }
  };

  // Auto-mute video when syncing (audio is master)
  useEffect(() => {
    handleVolumeChange(props.syncPreview ? 0 : 0.5);
    // eslint-disable-next-line
  }, [props.syncPreview]);

  // Pause the local video when parent requests a manual pause while unsynced
  useEffect(() => {
    if (props.syncPreview) return;
    if (props.manualPauseTick == null) return;

    const v = player.current;
    if (!v) return;

    v.pause();
    setIsPlaying(false);
  }, [props.manualPauseTick, props.syncPreview]);

  // Deterministic video play/pause while syncing (parent-driven)
  useEffect(() => {
    if (!props.syncPreview) return;
    const v = player.current;
    if (!v) return;
    if (props.playPausePlayer === "play") {
      v.play().then(() => setIsPlaying(true)).catch(() => { });
    } else {
      v.pause();
      setIsPlaying(false);
    }
  }, [props.playPausePlayer, props.syncPreview]);

  // Soft/Hard sync to audio master clock
  useEffect(() => {
    if (!props.syncPreview) return;
    if (props.masterMs == null) return;
    const v = player.current;
    if (!v) return;

    const videoMs = (v.currentTime || 0) * 1000;
    const drift = videoMs - props.masterMs;

    if (Math.abs(drift) > HARD_DRIFT_MS) {
      const now = performance.now();
      if (now - lastHardSyncAt.current > HARD_COOLDOWN_MS) {
        v.currentTime = props.masterMs / 1000;
        lastHardSyncAt.current = now;
        v.playbackRate = 1;
      }
      return;
    }

    if (Math.abs(drift) > SOFT_DRIFT_MS) {
      const correction = clamp(1 - drift / 800, 0.95, 1.05);
      if (!Number.isNaN(correction)) v.playbackRate = correction;
    } else {
      if (v.playbackRate !== audioSpeed) v.playbackRate = audioSpeed;
    }
  }, [props.masterMs, props.syncPreview, audioSpeed]);

  // Re-enabled: local toggle works in sync mode by delegating to parent
  const togglePlayPause = () => {
    const v = player.current;
    if (!v) return;

    if (props.syncPreview) {
      if (isPlaying) {
        // ask parent to pause audio; video will follow via playPausePlayer
        props.onSyncPause?.();
      } else {
        // ask parent to play audio
        props.onSyncPlay?.();
      }
      return;
    }

    // Non-sync mode: control the video locally
    if (isPlaying) {
      v.pause();
      setIsPlaying(false);
    } else {
      v.play().then(() => setIsPlaying(true)).catch(() => { });
    }
  };

  return (
    <Grid
      item
      container
      xs={12}
      sm={12}
      md={12}
      lg={12}
      xl={12}
      className={classes["AudioOutPutHorizontal"]}
      direction="row"
      width="100%"
      top={isScrolled}
    >
      <Grid item container className={classes["AudioOutPutVideoHorizontal"]}>
        <video
          ref={player}
          width="100%"
          height="100%"
          preload="auto"
          onLoadedMetadata={onLoadedMeta}
          onProgress={onProgress}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
        >
          <source src={props.source} type="video/mp4" />
        </video>
      </Grid>

      <Grid
        item
        container
        className={classes["AudioOutPutController"]}
        paddingTop={0}
        marginTop={-2.4}
      >
        <Grid
          item
          paddingLeft={1}
          paddingRight={1}
          sx={{ width: "100%", height: 20 }}
          bgcolor="#EBEBEB"
        >
          <VideoSeekSlider
            max={maxTime}
            currentTime={currentTime}
            bufferTime={progress}
            onChange={handleTimeChange}
            limitTimeTooltipBySides={true}
            secondsPrefix="00:"
            minutesPrefix="0:"
          />
        </Grid>

        <Grid
          item
          container
          direction="row"
          alignItems="center"
          gap={3}
          marginLeft={1}
          marginBottom={1}
        >
          <Grid item sx={{ color: `${primaryColor}`, cursor: "pointer" }}>
            <FastRewindIcon
              fontSize="large"
              onClick={() => handleTimeChange(currentTime - 100)}
            />
          </Grid>

          <Grid item sx={{ color: `${primaryColor}`, cursor: "pointer" }}>
            {isPlaying ? (
              <PauseCircleIcon fontSize="large" onClick={togglePlayPause} />
            ) : (
              <PlayCircleIcon fontSize="large" onClick={togglePlayPause} />
            )}
          </Grid>

          <Grid item sx={{ color: `${primaryColor}`, cursor: "pointer" }}>
            <FastForwardIcon
              fontSize="large"
              onClick={() => handleTimeChange(currentTime + 100)}
            />
          </Grid>

          <Grid
            item
            container
            justifyContent="center"
            alignItems="center"
            xs={1.5}
            gap={1}
          >
            <Grid item>
              <InputMask
                className={classes["VideoPlaceTimelineEditTranslation"]}
                mask="99:99:99.99"
                value={inputTime}
                onChange={handleTimeInputChange}
                onKeyPress={handleKeyPress}
                placeholder="HH:MM:SS.MS"
              />
            </Grid>
          </Grid>

          <Grid>Speed:</Grid>
          <Grid
            item
            container
            justifyContent="center"
            alignItems="center"
            marginRight={1}
            xs="auto"
          >
            <select
              value={audioSpeed}
              onChange={handleSpeedChange}
              className={classes["AudioOutPutShowTimeOptionBox"]}
            // NOTE: no longer disabled during sync
            >
              {PLAYBACK_SPEED_OPTIONS.map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Grid>

          <Grid item sx={{ width: 200 }}>
            <Stack spacing={2} direction="row" alignItems="center">
              <VolumeDown />
              <Slider
                value={volume}
                onChange={(_, val) => handleVolumeChange(val)}
                min={0}
                max={1}
                step={0.1}
                aria-label="Volume"
                sx={{ color: `${primaryColor}` }}
                disabled={props.syncPreview} // keep muted while syncing
              />
              <VolumeUp />
            </Stack>
          </Grid>

          <Grid item xs={1}>
            <label style={{ whiteSpace: "nowrap" }}>
              <input
                type="checkbox"
                checked={props.syncPreview}
                onChange={(e) => props.setSyncPreview(e.target.checked)}
              />
              {" "}
              <span>Preview</span>
            </label>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default PerformanceVideoPlayer;
